import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppLayout } from './components/layout/AppLayout';
import { ElevatedRoute } from './components/layout/ElevatedRoute';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { PageLoadingFallback } from './components/ui/loading-spinner';
import { AuthProvider } from './hooks/useAuth';
import { LocalModeProvider } from './hooks/useLocalMode';
import { AuthCallback } from './pages/Auth/Callback';
import Welcome from './pages/Welcome';

// Lazy load heavy pages to reduce initial bundle size
const Account = lazy(() => import('./pages/Account'));
const AdminPage = lazy(() => import('./pages/Admin'));
const AI = lazy(() => import('./pages/AI'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Config = lazy(() => import('./pages/Config'));
const Help = lazy(() => import('./pages/Help'));
const Patents = lazy(() => import('./pages/Patents'));
const Privacy = lazy(() => import('./pages/Privacy'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdmin'));
const Terms = lazy(() => import('./pages/Terms'));

// Remove old App.css, we'll use Tailwind
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider defaultTheme="system">
            <LocalModeProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Welcome />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/help" element={<Help />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />

                {/* Redirect old/problematic auth routes to landing page */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/signin" element={<Navigate to="/" replace />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />

                {/* Protected routes - all require authentication */}

                <Route
                  path="/patents"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <Patents />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* AI Assistant routes */}
                <Route
                  path="/ai/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <AI />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/account/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <Account />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <AdminPage />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Super Admin routes */}
                <Route
                  path="/superadmin/*"
                  element={
                    <ProtectedRoute requiredRole="superadmin">
                      <ElevatedRoute>
                        <AppLayout>
                          <Suspense fallback={<PageLoadingFallback />}>
                            <SuperAdminPage />
                          </Suspense>
                        </AppLayout>
                      </ElevatedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Analytics routes */}
                <Route
                  path="/analytics/*"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <Analytics />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Local config routes */}
                <Route
                  path="/config/*"
                  element={
                    <ProtectedRoute requiresLocal>
                      <AppLayout>
                        <Suspense fallback={<PageLoadingFallback />}>
                          <Config />
                        </Suspense>
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Default redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LocalModeProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
